/**
 * Schutz gegen Browser-Übersetzungs-Crashes (Google Translate / Chrome).
 *
 * Problem: Übersetzer ersetzen Textknoten im DOM durch eigene <font>-Wrapper.
 * React kennt diese Fremdknoten nicht mehr; beim Unmount wirft `removeChild`
 * dann `NotFoundError: ... not a child of this node` und die Seite crasht
 * (siehe Sentry-Issue, Radix SelectPortal beim Navigations-Unmount).
 *
 * Die <meta name="google" content="notranslate"> in index.html verhindert die
 * native Chrome-Übersetzung. Dieser Patch ist die zweite Verteidigungslinie
 * für Übersetzer-Extensions, die `notranslate` ignorieren: er lässt
 * `removeChild`/`insertBefore` bei einem Fremdknoten still zurückfallen statt
 * zu werfen. Offizieller, von React empfohlener Workaround.
 *
 * MUSS vor dem ersten React-Mount laufen (erste Import-Zeile in main.tsx).
 */
export function installDomTranslationGuard(): void {
  if (typeof Node !== 'function' || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      // Knoten wurde vom Übersetzer verschoben/ersetzt — still ignorieren.
      if (child.parentNode) {
        child.parentNode.removeChild(child);
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // Referenzknoten liegt nicht mehr hier — ohne Referenz anhängen.
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
